import React, { useState, useEffect } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import type { DastarkhanProject, CredentialTestResult, ClearResult } from '../../api/dastarkhan';
import { PasswordConfirmationModal } from './PasswordConfirmationModal';
import {
  Card,
  Input,
  Button,
  Select,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface ProjectSettingsProps {
  project: DastarkhanProject;
  canEdit: boolean;
  isAdmin?: boolean;
  onUpdated: () => void;
  onDeleted?: () => void;
}

export const ProjectSettings: React.FC<ProjectSettingsProps> = ({ project, canEdit, isAdmin = false, onUpdated, onDeleted }) => {
  const { theme } = useTheme();
  const { fetchData } = useAuthenticatedFetch();

  const [name, setName] = useState(project.name);
  const [matterCode, setMatterCode] = useState(project.matter_code || '');
  const [description, setDescription] = useState(project.description || '');
  const [status, setStatus] = useState(project.status);
  const [saving, setSaving] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailSuccess, setDetailSuccess] = useState(false);

  const [neo4jUri, setNeo4jUri] = useState('');
  const [neo4jUsername, setNeo4jUsername] = useState('');
  const [neo4jPassword, setNeo4jPassword] = useState('');
  const [credSaving, setCredSaving] = useState(false);
  const [credTesting, setCredTesting] = useState(false);
  const [credMessage, setCredMessage] = useState<string | null>(null);
  const [credStatus, setCredStatus] = useState<'connected' | 'failed' | null>(null);

  // Danger zone state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [dangerLoading, setDangerLoading] = useState(false);
  const [dangerMessage, setDangerMessage] = useState<string | null>(null);
  const [dangerIsError, setDangerIsError] = useState(false);

  useEffect(() => {
    setName(project.name);
    setMatterCode(project.matter_code || '');
    setDescription(project.description || '');
    setStatus(project.status);
  }, [project]);

  const handleSaveDetails = async () => {
    setSaving(true);
    setDetailError(null);
    setDetailSuccess(false);
    try {
      await fetchData(`/dastarkhan/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: name.trim(),
          matter_code: matterCode.trim() || undefined,
          description: description.trim() || undefined,
          status,
        }),
      });
      setDetailSuccess(true);
      onUpdated();
    } catch {
      setDetailError('Failed to save project details');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!neo4jUri.trim() || !neo4jUsername.trim() || !neo4jPassword.trim()) {
      setCredMessage('All credential fields are required');
      setCredStatus('failed');
      return;
    }
    setCredSaving(true);
    setCredMessage(null);
    try {
      const result = await fetchData(`/dastarkhan/projects/${project.id}/credentials`, {
        method: 'PUT',
        body: JSON.stringify({
          uri: neo4jUri.trim(),
          username: neo4jUsername.trim(),
          password: neo4jPassword.trim(),
        }),
      }) as CredentialTestResult | undefined;
      if (result) {
        setCredMessage(result.message);
        setCredStatus(result.status);
        if (result.status === 'connected') {
          setNeo4jUri('');
          setNeo4jUsername('');
          setNeo4jPassword('');
          onUpdated();
        }
      }
    } catch {
      setCredMessage('Failed to save credentials');
      setCredStatus('failed');
    } finally {
      setCredSaving(false);
    }
  };

  const handleTestCredentials = async () => {
    setCredTesting(true);
    setCredMessage(null);
    try {
      const result = await fetchData(`/dastarkhan/projects/${project.id}/credentials/test`, {
        method: 'POST',
        body: JSON.stringify({}),
      }) as CredentialTestResult | undefined;
      if (result) {
        setCredMessage(result.message);
        setCredStatus(result.status);
      }
    } catch {
      setCredMessage('Failed to test connectivity');
      setCredStatus('failed');
    } finally {
      setCredTesting(false);
    }
  };

  const msgColor = credStatus === 'connected'
    ? theme.colors.semantic.success.default
    : theme.colors.semantic.error.default;

  return (
    <Stack gap="lg">
      {/* Project Details */}
      <Card style={{ overflow: 'visible' }}>
        <Card.Body>
          <Stack gap="md">
            <h3 style={{ margin: 0, fontSize: '1rem', color: theme.colors.text.primary }}>Project Details</h3>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px' }}>Name</label>
              <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px' }}>Matter Code</label>
              <Input value={matterCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMatterCode(e.target.value)} disabled={!canEdit} placeholder="FTI-2026-0042" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px' }}>Description</label>
              <Input value={description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)} disabled={!canEdit} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px' }}>Status</label>
              <Select
                value={status}
                onChange={(value: string) => setStatus(value as 'active' | 'archived')}
                disabled={!canEdit}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'archived', label: 'Archived' },
                ]}
              />
            </div>
            {detailError && <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>{detailError}</div>}
            {detailSuccess && <div style={{ color: theme.colors.semantic.success.default, fontSize: '0.85rem' }}>Saved</div>}
            {canEdit && (
              <Flex justify="end">
                <Button variant="solid" color="primary" size="sm" onClick={handleSaveDetails} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Details'}
                </Button>
              </Flex>
            )}
          </Stack>
        </Card.Body>
      </Card>

      {/* Danger Zone — Admin Only */}
      {isAdmin && (
        <Card>
          <Card.Body>
            <Stack gap="md">
              <h3 style={{ margin: 0, fontSize: '1rem', color: theme.colors.semantic.error.default }}>Danger Zone</h3>
              {dangerMessage && (
                <div style={{ color: dangerIsError ? theme.colors.semantic.error.default : theme.colors.semantic.success.default, fontSize: '0.85rem' }}>
                  {dangerMessage}
                </div>
              )}
              <Flex gap="sm">
                <Button
                  variant="outline"
                  color="error"
                  size="sm"
                  onClick={() => { setShowClearModal(true); setDangerMessage(null); }}
                  disabled={dangerLoading}
                >
                  Clear Project Contents
                </Button>
                <Button
                  variant="outline"
                  color="error"
                  size="sm"
                  onClick={() => { setShowDeleteModal(true); setDangerMessage(null); }}
                  disabled={dangerLoading}
                >
                  Delete Project
                </Button>
              </Flex>
            </Stack>
          </Card.Body>
        </Card>
      )}

      <PasswordConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Project"
        description="This will permanently delete the project and all associated data (graph, audit log, chat sessions, memberships). This cannot be undone."
        confirmLabel="Delete Project"
        loading={dangerLoading}
        onConfirm={async () => {
          setDangerLoading(true);
          try {
            await fetchData(`/dastarkhan/projects/${project.id}`, { method: 'DELETE' });
            setShowDeleteModal(false);
            onDeleted?.();
          } catch (err: unknown) {
            const msg = (err && typeof err === 'object' && 'message' in err) ? (err as { message: string }).message : 'Failed to delete project';
            setDangerMessage(msg);
            setDangerIsError(true);
            setShowDeleteModal(false);
          } finally {
            setDangerLoading(false);
          }
        }}
      />

      <PasswordConfirmationModal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        title="Clear Project Contents"
        description="This will delete all graph data, audit log entries, and chat sessions. Project settings, Neo4j credentials, and team members will be preserved."
        confirmLabel="Clear Contents"
        loading={dangerLoading}
        onConfirm={async () => {
          setDangerLoading(true);
          try {
            const result = await fetchData(`/dastarkhan/projects/${project.id}/clear`, {
              method: 'POST',
              body: JSON.stringify({}),
            }) as ClearResult | undefined;
            setShowClearModal(false);
            if (result) {
              setDangerMessage(`Cleared: ${result.cleared.neo4j_nodes} nodes, ${result.cleared.neo4j_edges} edges`);
              setDangerIsError(false);
            }
            onUpdated();
          } catch (err: unknown) {
            const msg = (err && typeof err === 'object' && 'message' in err) ? (err as { message: string }).message : 'Failed to clear project contents';
            setDangerMessage(msg);
            setDangerIsError(true);
            setShowClearModal(false);
          } finally {
            setDangerLoading(false);
          }
        }}
      />

      {/* Neo4j Credentials */}
      {canEdit && (
        <Card>
          <Card.Body>
            <Stack gap="md">
              <Flex justify="between" align="center">
                <h3 style={{ margin: 0, fontSize: '1rem', color: theme.colors.text.primary }}>Neo4j Aura Credentials</h3>
                {project.has_credentials && (
                  <span style={{
                    padding: `2px ${spacing.sm}`,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: project.neo4j_connected
                      ? theme.colors.semantic.success.light
                      : theme.colors.semantic.warning.light,
                    color: project.neo4j_connected
                      ? theme.colors.semantic.success.default
                      : theme.colors.semantic.warning.default,
                  }}>
                    {project.neo4j_connected ? 'Connected' : 'Configured'}
                  </span>
                )}
              </Flex>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px' }}>URI</label>
                <Input value={neo4jUri} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNeo4jUri(e.target.value)} placeholder="neo4j+s://xxxxxxxx.databases.neo4j.io" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px' }}>Username</label>
                <Input value={neo4jUsername} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNeo4jUsername(e.target.value)} placeholder="neo4j" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px' }}>Password</label>
                <Input type="password" value={neo4jPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNeo4jPassword(e.target.value)} placeholder="Enter password" />
              </div>
              {credMessage && <div style={{ color: msgColor, fontSize: '0.85rem' }}>{credMessage}</div>}
              <Flex justify="end" gap="sm">
                {project.has_credentials && (
                  <Button variant="outline" size="sm" onClick={handleTestCredentials} disabled={credTesting}>
                    {credTesting ? 'Testing...' : 'Test Connection'}
                  </Button>
                )}
                <Button variant="solid" color="primary" size="sm" onClick={handleSaveCredentials} disabled={credSaving}>
                  {credSaving ? 'Saving...' : 'Save Credentials'}
                </Button>
              </Flex>
            </Stack>
          </Card.Body>
        </Card>
      )}
    </Stack>
  );
};
